import { useEffect, useRef, useState } from 'react'

import { prefersReducedMotion, useReducedMotion } from './motion'

/**
 * A formatted figure that counts to its value.
 *
 * It takes the text the caller already formatted — "$350.00", "11 days",
 * "$0.84/hour" — and animates only the first number inside it, keeping the
 * caller's decimals, grouping, prefix and suffix. The formatting stays where
 * it is authoritative (core/pricing and friends); this component never
 * computes a figure, it only travels to one.
 *
 * At rest it renders the text exactly as given, as plain text, so a test, a
 * screen reader and a copy-paste all read the real value. While it moves the
 * moving copy is hidden from assistive technology and the real value sits
 * beside it in `.sr-only`, so nothing is ever announced half-way.
 *
 * When the value changes — a refetch moved the balance — it counts from the
 * figure on screen to the new one, which is how a reader notices that it
 * changed. With reduced motion, or no animation frames, it simply renders.
 */
const NUMBER = /\d[\d,]*(?:\.\d+)?/

interface Parsed {
  prefix: string
  value: number
  decimals: number
  grouped: boolean
  suffix: string
}

export function parseFigure(text: string): Parsed | null {
  const match = NUMBER.exec(text)
  if (!match) return null
  const raw = match[0]
  const value = Number(raw.replace(/,/g, ''))
  if (!Number.isFinite(value)) return null
  const decimals = raw.includes('.') ? (raw.split('.')[1]?.length ?? 0) : 0
  return { prefix: text.slice(0, match.index), value, decimals, grouped: raw.includes(','), suffix: text.slice(match.index + raw.length) }
}

export function formatFigure(parsed: Parsed, value: number): string {
  const number = value.toLocaleString('en-US', {
    minimumFractionDigits: parsed.decimals,
    maximumFractionDigits: parsed.decimals,
    useGrouping: parsed.grouped,
  })
  return `${parsed.prefix}${number}${parsed.suffix}`
}

/** Exponential ease-out: most of the distance early, a long settle at the end. */
const settle = (t: number) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t))

function canAnimate(): boolean {
  return !prefersReducedMotion() && typeof window.requestAnimationFrame === 'function'
}

export function Figure({ text, duration = 1100 }: { text: string; duration?: number }) {
  const reduced = useReducedMotion()
  const [moving, setMoving] = useState<string | null>(() => {
    const parsed = parseFigure(text)
    return parsed && parsed.value !== 0 && canAnimate() ? formatFigure(parsed, 0) : null
  })
  // The number on screen right now, so a change counts from where the reader is.
  const shown = useRef<number | null>(null)

  useEffect(() => {
    const parsed = parseFigure(text)
    if (!parsed || reduced || !canAnimate()) {
      shown.current = parsed?.value ?? null
      setMoving(null)
      return undefined
    }
    const from = shown.current ?? 0
    const to = parsed.value
    if (from === to) {
      setMoving(null)
      return undefined
    }
    let frame = 0
    const start = performance.now()
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration)
      if (progress >= 1) {
        shown.current = to
        setMoving(null)
        return
      }
      const value = from + (to - from) * settle(progress)
      shown.current = value
      setMoving(formatFigure(parsed, value))
      frame = window.requestAnimationFrame(tick)
    }
    frame = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(frame)
  }, [text, duration, reduced])

  if (moving === null) return <>{text}</>
  return (
    <>
      <span aria-hidden="true" className="figure-moving">{moving}</span>
      <span className="sr-only">{text}</span>
    </>
  )
}
